import React, { useState } from 'react';
import { Table, Button, Badge, Spinner, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faMinus, faTrash, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { inventoryService } from '../../services/firestore';
import './PartsTable.scss';
import { useTranslation } from 'react-i18next';

function PartsTable({ parts, loading, onUpdate, selectedModel }) {
  const { t } = useTranslation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [partToDelete, setPartToDelete] = useState(null);

  // Funzione per ordinare i ricambi
  const sortedParts = [...parts].sort((a, b) => {
    // Prima ordina per modello
    const modelCompare = a.model.localeCompare(b.model);
    if (modelCompare !== 0) return modelCompare;
    // Se i modelli sono uguali, ordina per tipo
    return a.type.localeCompare(b.type);
  });

  const getPartTypeTranslation = (type) => {
    return t(`models.part.types.${type}`);
  };

  const handleQuantityChange = async (partId, increment) => {
    try {
      const part = parts.find(p => p.id === partId);
      if (!part) {
        console.error('Part not found:', partId);
        return;
      }

      const newQuantity = part.quantity + increment;
      if (newQuantity < 0) {
        console.warn('Cannot set negative quantity');
        return;
      }

      // Aggiorniamo ottimisticamente l'UI
      const updatedPart = { ...part, quantity: newQuantity };
      
      try {
        await inventoryService.update(partId, updatedPart);
        onUpdate(); // Ricarica i dati dopo l'aggiornamento
      } catch (error) {
        console.error('Error updating quantity:', error);
        // In caso di errore, potremmo mostrare un messaggio all'utente
        throw error;
      }
    } catch (error) {
      console.error('Error in quantity change:', error);
    }
  };

  const handleDeleteClick = (part) => {
    setPartToDelete(part);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!partToDelete) return;
    
    try {
      await inventoryService.delete(partToDelete.id);
      await onUpdate();
      setShowDeleteModal(false);
      setPartToDelete(null);
    } catch (error) {
      console.error('Errore durante l\'eliminazione:', error);
      // Qui potresti aggiungere una gestione degli errori più sofisticata
    }
  };

  const getStockStatus = (part) => {
    if (!part.minThreshold) return null;
    
    if (part.quantity <= 0) {
      return 'danger';
    } else if (part.quantity <= part.minThreshold) {
      return 'warning';
    }
    return null;
  };

  return (
    <>
      <div className="parts-list">
        <div className="table-responsive">
          <Table hover className="parts-table">
            <thead>
              <tr>
                <th className="ps-4">{t('inventory.table.brand')}</th>
                <th>{t('inventory.table.model')}</th>
                <th>{t('inventory.table.type')}</th>
                <th className="text-center">{t('inventory.table.quantity')}</th>
                <th className="text-center pe-4">{t('inventory.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {sortedParts.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-5 text-muted">
                    <div className="empty-state">
                      {selectedModel 
                        ? t('inventory.table.noPartsFound')
                        : t('inventory.table.selectModelPrompt')
                      }
                    </div>
                  </td>
                </tr>
              ) : (
                sortedParts.map(part => {
                  const stockStatus = getStockStatus(part);
                  return (
                    <tr key={part.id}>
                      <td className="ps-4">{part.brand}</td>
                      <td>{part.model}</td>
                      <td>{getPartTypeTranslation(part.type)}</td>
                      <td className="text-center">
                        <div className={`quantity-control ${stockStatus ? `status-${stockStatus}` : ''}`}>
                          <Button
                            variant="light"
                            className="quantity-btn"
                            onClick={() => handleQuantityChange(part.id, -1)}
                            disabled={part.quantity <= 0}
                          >
                            <FontAwesomeIcon icon={faMinus} />
                          </Button>
                          <span className="quantity-value">{part.quantity}</span>
                          <Button
                            variant="light"
                            className="quantity-btn"
                            onClick={() => handleQuantityChange(part.id, 1)}
                          >
                            <FontAwesomeIcon icon={faPlus} />
                          </Button>
                        </div>
                      </td>
                      <td className="text-center pe-4">
                        <div className="d-flex justify-content-center">
                          <Button 
                            variant="light" 
                            className="delete-btn"
                            onClick={() => handleDeleteClick(part)}
                          >
                            <FontAwesomeIcon icon={faTrash} className="text-danger" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>
      </div>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('inventory.deleteModal.title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {`${t('inventory.deleteModal.message.prefix')} ${getPartTypeTranslation(partToDelete?.type || '')} ${partToDelete?.model || ''}${t('inventory.deleteModal.message.suffix')}`}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            {t('inventory.deleteModal.buttons.cancel')}
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            {t('inventory.deleteModal.buttons.confirm')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default PartsTable; 